import { Controller, useFormContext } from "react-hook-form";
import { classNames } from "primereact/utils";
import { useAppContext } from "../../../../../AppContext";
import { AppTooltip } from "../../../tooltip/AppTooltip";
import { Icon, IconType } from "../../../../icons/Icon";
import { RenderWhen } from "../../../../infrastructure/components/RenderWhen";
import { BarcodeScannerModal } from "./BarcodeScannerModal";
import { useBarcodeField } from "./hooks/useBarcodeField";
import styles from "./BarcodeField.module.scss";

import { dvSize } from "../../../../infrastructure/utilities/viewportUnitsUtility";
interface IBarcodeFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  tooltip?: string;
  required?: boolean;
  disabled?: boolean;
  width?: string;
  /** Fires on every successful scan (camera, file fallback, or USB/Enter). */
  onScanned?: (value: string) => void;
}

/**
 * React-hook-form field for barcodes with a built-in camera scanner.
 *
 * USB scanners work out of the box — they focus the native input, type the
 * value and press Enter. The `Enter` handler fires `onScanned(value)` and
 * prevents the surrounding form from being submitted by the scanner.
 *
 * The camera icon opens `BarcodeScannerModal`, which handles the
 * live-video / file-fallback flow. All logic lives in hooks; this
 * component is only JSX wiring.
 */
export const BarcodeField = ({
  name,
  label,
  placeholder,
  tooltip,
  required = false,
  disabled = false,
  width,
  onScanned,
}: IBarcodeFieldProps) => {
  const { control } = useFormContext();
  const { isDesktopOrTablet } = useAppContext();

  const {
    isScannerOpen,
    openScanner,
    closeScanner,
    handleKeyDown,
    handleScannerResult,
  } = useBarcodeField({ name, onScanned });

  if (!width) {
    width = isDesktopOrTablet ? "600px" : dvSize("80dvw");
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <>
          <div
            style={{ width, maxWidth: width }}
            className={classNames(styles.root, {
              [styles.root_disabled]: disabled,
            })}
          >
            <RenderWhen condition={!!label}>
              <div
                className={classNames(styles.root_label, {
                  [styles.root_label_interacted_with]:
                    !!field.value && field.value !== "",
                  [styles.root_label_error]: !!fieldState.error?.message,
                })}
              >
                {label}
                {required ? "*" : ""}
                <AppTooltip content={tooltip} />
              </div>
            </RenderWhen>

            <div className={styles.root_input_container}>
              <input
                id={field.name}
                name={field.name}
                type="text"
                className={styles.root_input}
                placeholder={placeholder}
                disabled={disabled}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                onKeyDown={(e) => handleKeyDown(e, field.value)}
                autoComplete="off"
              />
              <RenderWhen condition={!disabled}>
                <button
                  type="button"
                  className={styles.root_camera_button}
                  onClick={openScanner}
                  aria-label="Сканиране с камера"
                >
                  <Icon type={IconType.Camera} width="20px" height="20px" />
                </button>
              </RenderWhen>
            </div>
          </div>

          <BarcodeScannerModal
            visible={isScannerOpen}
            onHide={closeScanner}
            onScanned={(value) => handleScannerResult(value, field.onChange)}
          />
        </>
      )}
    />
  );
};
